import "./SelectOptionsPanel.css";
import React, {memo, useCallback, useRef} from "react";
import {faFileExport, faFileImport, faPlusCircle, IconDefinition} from "@fortawesome/free-solid-svg-icons";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {DivisionFull} from "../../../Utils/response-types";
import {DataLoadingStatus} from "../../../Utils/enums";
import {createDivision, exportKeywords, uploadCompressedKeywords} from "../../../Utils/queries";


type ActionButton = {
    actionName: string,
    icon: IconDefinition,
    performAction: () => void
};

const SelectDivisionPanel = ({divisions, setDivisions, selectedIdx, setSelectedIdx, setStatus}: {divisions: DivisionFull[], setDivisions: React.Dispatch<React.SetStateAction<DivisionFull[]>>, selectedIdx: number, setSelectedIdx: React.Dispatch<React.SetStateAction<number>>, setStatus: React.Dispatch<React.SetStateAction<DataLoadingStatus>> }) => {
    const inputRef = useRef<HTMLInputElement>(null);

    const handleAddDivision = useCallback(() => {
        const new_div_name = prompt("Enter the division name:");
        if (new_div_name !== null && new_div_name.length > 0) {
            setStatus(DataLoadingStatus.Loading);
            const old_div_list = [...divisions];
            setDivisions([...old_div_list, {division_id: -1, division_name: new_div_name, attributes: [], categories: []}]);
            createDivision(new_div_name).then(resp => {
                if (resp.errorCode !== undefined || !resp.success) {
                    alert(resp.message || `Error: ${resp.errorCode}`);
                    setDivisions(old_div_list);
                    setStatus(DataLoadingStatus.Loaded);
                } else {
                    alert("Division created successfully!");
                    setStatus(DataLoadingStatus.NotLoaded);
                }
            });
        } else {
            alert("Cannot create the attribute without name!");
        }
    }, [divisions, setStatus, setDivisions]);

    const handleKeywordsDataExport = useCallback(() => {
        setStatus(DataLoadingStatus.Loading);
        exportKeywords(false).then(resp => {
            exportKeywords(true).then(resp_r => {
                if (!resp || !resp_r) {
                   alert("An error occurred! Please, reload the page and try again or contact the administrator!");
                }
                setStatus(DataLoadingStatus.Loaded);
            });
        });
    }, [setStatus]);

    const handleFileSelect = (selectedFile: File | null) => {
        if (selectedFile && selectedFile.name.toLowerCase().endsWith('.json')) {
            // eslint-disable-next-line no-restricted-globals
            const confirmed = confirm("Do you really want to replace all the settings in the DB with this content? This action cannot be undone!");
            if (confirmed) {
                uploadCompressedKeywords(selectedFile).then(resp => {
                    if (resp.errorCode !== undefined) {
                        alert(`Error: ${resp.errorCode}`);
                    } else {
                        alert("Keywords successfully imported!")
                        setStatus(DataLoadingStatus.NotLoaded);
                    }
                });
            }
        } else {
            alert('Please select a .json file.');
        }
    };

    const additionalButtonActions: ActionButton[] = [
        {actionName: "Add Division", icon: faPlusCircle, performAction: handleAddDivision},
        {actionName: "Export Keywords", icon: faFileExport, performAction: handleKeywordsDataExport},
        {actionName: "Import Keywords", icon: faFileImport, performAction: () => inputRef.current?.click()},
    ];

    return (
        <>
            <input ref={inputRef} type="file" accept=".json" hidden
                onChange={(e) => {
                    handleFileSelect(e.target.files?.[0] ?? null);
                    if (e.target) e.target.value = '';
                }}
            />
            <SelectOptionsPanel selectedIdx={selectedIdx} setSelectedIdx={setSelectedIdx} options={divisions.map(d => d.division_name)} actions={additionalButtonActions}/>
        </>
    );
};

const SelectOptionsPanel = ({options, actions, selectedIdx, setSelectedIdx}: {
    options: string[],
    actions: ActionButton[],
    selectedIdx: number,
    setSelectedIdx: React.Dispatch<React.SetStateAction<number>>
}) => {
    return (
        <div className="select-option-panel">
            {(options).map((option, idx) => <SelectOptionsButton key={option} option={option} isSelected={idx === selectedIdx} makeSelected={() => setSelectedIdx(idx)}/>)}
            {(actions).map((action) => <SelectOptionsActionButton key={action.actionName} actionName={action.actionName} icon={action.icon} performAction={action.performAction} />)}
        </div>
    );
};

const SelectOptionsButton = ({option, isSelected, makeSelected}: {option: string, isSelected: boolean, makeSelected: () => void}) => (
    <div
        className={"select-option-panel-button option-panel-button-"+(isSelected? "":"un")+"selected"}
        onClick={() => makeSelected()}>
        <p>{option}</p>
    </div>
);

const SelectOptionsActionButton = ({actionName, icon, performAction}: {actionName: string, icon: IconDefinition, performAction: () => void}) => (
    <div
        className={"select-option-panel-action-button"}
        title={actionName}
        onClick={performAction}>
        <FontAwesomeIcon icon={icon}/>
    </div>
);

export default memo(SelectDivisionPanel);
